import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { money } from "@/lib/constants";
import { Loader2, DollarSign, AlertCircle, FileText, Wrench, TrendingUp } from "lucide-react";

const Stat = ({ icon: Icon, label, value, tone = "royal", testId }) => (
  <div className="rounded-lg border border-border bg-white p-5" data-testid={testId}>
    <div className="flex items-center justify-between">
      <p className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground font-semibold">{label}</p>
      <Icon className={`h-4 w-4 ${tone === "gold" ? "text-gold" : tone === "red" ? "text-red-600" : "text-royal"}`} />
    </div>
    <p className="font-display text-3xl font-bold text-royal-deep mt-3 leading-none">{value}</p>
  </div>
);

export default function Finance() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/finance/summary").then(({ data }) => setData(data));
  }, []);

  if (!data) return <Loader2 className="h-6 w-6 animate-spin text-royal" />;

  return (
    <div className="space-y-8" data-testid="finance-page">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-gold font-semibold">Money</p>
        <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-royal-deep mt-2">Financial Dashboard</h1>
        <p className="text-muted-foreground mt-3 text-sm md:text-base">
          What has been earned, what is still owed, and which properties are actually profitable.
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <Stat icon={DollarSign} label="Revenue collected" value={money(data.revenue_all_time)}
              testId="stat-revenue" />
        <Stat icon={TrendingUp} label="Collected this month" value={money(data.revenue_this_month)} tone="gold"
              testId="stat-revenue-month" />
        <Stat icon={FileText} label="Outstanding" value={money(data.outstanding)} testId="stat-outstanding" />
        <Stat icon={AlertCircle} label={`Overdue (${data.overdue_count})`} value={money(data.overdue)} tone="red"
              testId="stat-overdue" />
        <Stat icon={Wrench} label="Labour & vendor cost" value={money(data.labour_cost)} testId="stat-labour" />
        <Stat icon={FileText} label="Drafts waiting" value={data.draft_count} tone="gold" testId="stat-drafts" />
      </div>

      <section>
        <div className="flex items-center gap-3 mb-3">
          <h2 className="font-display text-base md:text-lg font-semibold text-royal-deep">Property performance</h2>
          <div className="h-px flex-1 bg-border" />
          <Link to="/invoices" data-testid="finance-invoices-link"
                className="text-xs font-semibold text-royal hover:text-gold transition-colors duration-200">
            Invoices →
          </Link>
        </div>
        {data.properties.length === 0 ? (
          <p className="text-sm text-muted-foreground">No completed work with costs recorded yet.</p>
        ) : (
          <div className="hidden md:block rounded-lg border border-border bg-white overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-secondary/60">
                <tr className="text-left">
                  {["Property", "Work orders", "Cost", "Billed", "Margin"].map((h) => (
                    <th key={h} className="px-4 py-2.5 text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {data.properties.map((p) => (
                  <tr key={p.property_id} data-testid={`finance-property-${p.property_id}`}>
                    <td className="px-4 py-3">
                      <Link to={`/properties/${p.property_id}`} className="font-medium text-royal-deep hover:text-gold transition-colors duration-200">
                        {p.property_name}
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{p.work_orders}</td>
                    <td className="px-4 py-3 text-muted-foreground">{money(p.labour_cost)}</td>
                    <td className="px-4 py-3 text-royal-deep">{money(p.billed)}</td>
                    <td className={`px-4 py-3 font-semibold ${p.margin >= 0 ? "text-emerald-700" : "text-red-600"}`}>
                      {money(p.margin)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {data.properties.length > 0 && (
          <div className="md:hidden space-y-3">
            {data.properties.map((p) => (
              <div key={p.property_id} className="rounded-lg border border-border bg-white p-4"
                   data-testid={`finance-property-card-${p.property_id}`}>
                <Link to={`/properties/${p.property_id}`}
                      className="text-sm font-semibold text-royal-deep hover:text-gold break-words transition-colors duration-200">
                  {p.property_name}
                </Link>
                <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2.5">
                  {[["Work orders", p.work_orders], ["Cost", money(p.labour_cost)],
                    ["Billed", money(p.billed)], ["Margin", money(p.margin)]].map(([k, v]) => (
                    <div key={k}>
                      <dt className="text-[10px] uppercase tracking-wider text-muted-foreground">{k}</dt>
                      <dd className={`text-sm font-medium mt-0.5 ${
                        k === "Margin" ? (p.margin >= 0 ? "text-emerald-700" : "text-red-600") : "text-royal-deep"}`}>
                        {v}
                      </dd>
                    </div>
                  ))}
                </dl>
              </div>
            ))}
          </div>
        )}
      </section>

      <section>
        <div className="flex items-center gap-3 mb-3">
          <h2 className="font-display text-base md:text-lg font-semibold text-royal-deep">Who owes what</h2>
          <div className="h-px flex-1 bg-border" />
        </div>
        {data.owner_balances.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nothing outstanding — every invoice is settled.</p>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {data.owner_balances.map((o) => (
              <div key={o.owner_name} className="rounded-lg border border-border bg-white p-4"
                   data-testid={`owner-balance-${o.owner_name}`}>
                <p className="font-medium text-royal-deep">{o.owner_name}</p>
                <p className="font-display text-2xl font-bold text-royal mt-1">{money(o.balance)}</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  across {o.invoices} invoice{o.invoices === 1 ? "" : "s"}
                </p>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
